import React, { useEffect, useRef, useState } from "react";
import Icon from "../../atoms/Icon/Icon.component";
import tbIcon from "../../../static/tb-icon.png";
import {
  Caption,
  Card,
  CardBody,
  CardHeader,
  Caret,
  Cursor,
  EventTitle,
  Frame,
  HeaderMark,
  NpsScaleRow,
  NpsTicks,
  NpsThumb,
  NpsTrack,
  OptionsList,
  Page,
  PollOption,
  QuestionText,
  SaveTimerFill,
  SaveTimerTrack,
  ScrollContent,
  StaticBody,
  StaticCard,
  SubCard,
  SubmitButton,
  SubmitRow,
  TypedTextBox,
  VoteCheck,
  VoteCount,
  VoteRow,
} from "./AttendeeDemo.styled";

/* ---------------------------------------------------------------------------
 * Demo content - fictional event, presentational only. No API calls, no
 * real data: every number below is fake local state for the loop.
 * ------------------------------------------------------------------------- */
const EVENT_TITLE = "Product Summit 2026 · Day 2";
const POLL_QUESTION = "Which session was most useful?";
const POLL_OPTIONS = [
  "AI in product teams",
  "Pricing that scales",
  "Customer research live",
];
const POLL_TAP_INDEX = 1;
const POLL_VOTES_START = 128;
const POLL_VOTES_END = 129;

const NPS_QUESTION = "How likely are you to recommend this event?";
const NPS_START_VALUE = 4;
const NPS_END_VALUE = 9;
const NPS_TICKS = Array.from({ length: 11 }, (_, i) => i);
const NPS_VOTES_START = 211;
const NPS_VOTES_END = 212;

const TEXT_QUESTION = "What should we change for next year?";
const TEXT_FULL = "More time for Q&A a";

const CAPTION_TEXT = "2 of 3 answers saved";

const ARIA_LABEL =
  `Tendbe attendee screen for ${EVENT_TITLE}: a poll on which session was ` +
  `most useful, a recommendation rating, and an open text field. Answers ` +
  `are saved one by one as they're given, so they still count even if the ` +
  `attendee leaves before finishing.`;

const TYPE_CHAR_MS = 95;

interface IPollCardProps {
  selected: boolean;
  saving: boolean;
  saved: boolean;
  votes: number;
  showCursor: boolean;
}

const PollCard = ({ selected, saving, saved, votes, showCursor }: IPollCardProps) => (
  <SubCard>
    <QuestionText>{POLL_QUESTION}</QuestionText>
    <OptionsList>
      {POLL_OPTIONS.map((option, index) => (
        <PollOption key={option} $selected={selected && index === POLL_TAP_INDEX}>
          {option}
          {index === POLL_TAP_INDEX && (
            <Cursor $visible={showCursor} style={{ right: 8, top: "50%" }} />
          )}
        </PollOption>
      ))}
    </OptionsList>
    <SaveTimerTrack>
      <SaveTimerFill $active={saving || saved} />
    </SaveTimerTrack>
    <VoteRow>
      <VoteCheck $saved={saved}>
        <Icon name="check" />
      </VoteCheck>
      <VoteCount>{saved ? "Vote submitted" : `${votes} votes`}</VoteCount>
    </VoteRow>
  </SubCard>
);

interface INpsCardProps {
  value: number;
  saving: boolean;
  saved: boolean;
  votes: number;
  showCursor: boolean;
}

const NpsCard = ({ value, saving, saved, votes, showCursor }: INpsCardProps) => {
  const pct = (value / 10) * 100;
  return (
    <SubCard>
      <QuestionText>{NPS_QUESTION}</QuestionText>
      <NpsTrack>
        <NpsThumb $pct={pct} />
        <Cursor
          $visible={showCursor}
          style={{ left: `${pct}%`, top: "50%", transition: "left 0.9s ease, opacity 0.25s ease" }}
        />
      </NpsTrack>
      <NpsTicks>
        {NPS_TICKS.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </NpsTicks>
      <NpsScaleRow>
        <span>Not likely</span>
        <span>Very likely</span>
      </NpsScaleRow>
      <SaveTimerTrack>
        <SaveTimerFill $active={saving || saved} />
      </SaveTimerTrack>
      <VoteRow>
        <VoteCheck $saved={saved}>
          <Icon name="check" />
        </VoteCheck>
        <VoteCount>{saved ? "Vote submitted" : `${votes} votes`}</VoteCount>
      </VoteRow>
    </SubCard>
  );
};

interface ITextCardProps {
  typedChars: number;
  caretBlink: boolean;
  showCursor: boolean;
}

const TextCard = ({ typedChars, caretBlink, showCursor }: ITextCardProps) => (
  <SubCard>
    <QuestionText>{TEXT_QUESTION}</QuestionText>
    <TypedTextBox>
      {TEXT_FULL.slice(0, typedChars)}
      <Caret $blink={caretBlink} />
      <Cursor $visible={showCursor} style={{ left: 12, top: 16 }} />
    </TypedTextBox>
    <SubmitRow>
      <SubmitButton>Submit</SubmitButton>
    </SubmitRow>
  </SubCard>
);

/**
 * Animated replica of Tendbe's attendee answering screen, for the
 * "Designed for the way people participate" section. Fake local state only
 * - no API calls. The loop exists to communicate one thing: answers are
 * saved one by one, so an attendee who leaves halfway still has their
 * answers counted.
 *
 * Starts when the panel scrolls into view, pauses on hover, and loops
 * continuously (~10s per run) for as long as it stays on screen. With
 * prefers-reduced-motion, it skips the animation and renders the end state
 * directly: two answers saved, the text half-typed, caption visible.
 */
const AttendeeDemo = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [reduced] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  const shouldRunRef = useRef(false);

  const [pollSelected, setPollSelected] = useState(false);
  const [pollSaving, setPollSaving] = useState(false);
  const [pollSaved, setPollSaved] = useState(false);
  const [pollCursor, setPollCursor] = useState(false);

  const [npsValue, setNpsValue] = useState(NPS_START_VALUE);
  const [npsSaving, setNpsSaving] = useState(false);
  const [npsSaved, setNpsSaved] = useState(false);
  const [npsCursor, setNpsCursor] = useState(false);

  const [scrollStage, setScrollStage] = useState(0);
  const [typedChars, setTypedChars] = useState(0);
  const [textCursor, setTextCursor] = useState(false);

  const [leaving, setLeaving] = useState(false);
  const [showCaption, setShowCaption] = useState(false);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.4 }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    shouldRunRef.current = inView && !hovered && !reduced;
  }, [inView, hovered, reduced]);

  useEffect(() => {
    if (reduced) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: number;

    const reset = () => {
      setPollSelected(false);
      setPollSaving(false);
      setPollSaved(false);
      setPollCursor(false);
      setNpsValue(NPS_START_VALUE);
      setNpsSaving(false);
      setNpsSaved(false);
      setNpsCursor(false);
      setScrollStage(0);
      setTypedChars(0);
      setTextCursor(false);
      setLeaving(false);
      setShowCaption(false);
    };

    const typeText = (onDone: () => void) => {
      let i = 0;
      const tick = () => {
        if (cancelled) {
          return;
        }
        i += 1;
        setTypedChars(i);
        if (i < TEXT_FULL.length) {
          timeoutId = window.setTimeout(tick, TYPE_CHAR_MS);
        } else {
          onDone();
        }
      };
      timeoutId = window.setTimeout(tick, TYPE_CHAR_MS);
    };

    const steps: Array<{ run: () => void; wait: number }> = [
      { run: reset, wait: 1000 },
      { run: () => setPollCursor(true), wait: 700 },
      { run: () => setPollSelected(true), wait: 250 },
      { run: () => setPollSaving(true), wait: 900 },
      {
        run: () => {
          setPollSaving(false);
          setPollSaved(true);
          setPollCursor(false);
        },
        wait: 1300,
      },
      { run: () => setScrollStage(1), wait: 850 },
      { run: () => setNpsCursor(true), wait: 400 },
      { run: () => setNpsValue(NPS_END_VALUE), wait: 950 },
      { run: () => setNpsSaving(true), wait: 900 },
      {
        run: () => {
          setNpsSaving(false);
          setNpsSaved(true);
          setNpsCursor(false);
        },
        wait: 1300,
      },
      { run: () => setScrollStage(2), wait: 850 },
      { run: () => setTextCursor(true), wait: 500 },
      // Typing manages its own sub-chain; `wait` below is a placeholder,
      // replaced by the real typing duration once scheduled.
      { run: () => setTextCursor(false), wait: 0 },
      { run: () => {}, wait: 1200 },
      { run: () => setLeaving(true), wait: 950 },
      { run: () => setShowCaption(true), wait: 1900 },
    ];

    const TYPING_STEP_INDEX = 12;

    const runStep = (index: number) => {
      if (cancelled) {
        return;
      }
      if (!shouldRunRef.current) {
        timeoutId = window.setTimeout(() => runStep(index), 200);
        return;
      }

      const step = steps[index % steps.length];
      step.run();

      if (index % steps.length === TYPING_STEP_INDEX) {
        typeText(() => {
          if (!cancelled) {
            runStep(index + 1);
          }
        });
        return;
      }

      timeoutId = window.setTimeout(
        () => runStep(index + 1),
        step.wait
      );
    };

    runStep(0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [reduced]);

  if (reduced) {
    return (
      <Frame ref={rootRef}>
        <StaticCard role="img" aria-label={ARIA_LABEL}>
          <CardHeader>
            <HeaderMark src={tbIcon} alt="" aria-hidden="true" />
          </CardHeader>
          <StaticBody>
            <EventTitle>{EVENT_TITLE}</EventTitle>
            <PollCard
              selected
              saving={false}
              saved
              votes={POLL_VOTES_END}
              showCursor={false}
            />
            <NpsCard
              value={NPS_END_VALUE}
              saving={false}
              saved
              votes={NPS_VOTES_END}
              showCursor={false}
            />
            <TextCard typedChars={TEXT_FULL.length} caretBlink={false} showCursor={false} />
          </StaticBody>
        </StaticCard>
        <Caption $visible>{CAPTION_TEXT}</Caption>
      </Frame>
    );
  }

  return (
    <Frame
      ref={rootRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Card role="img" aria-label={ARIA_LABEL}>
        <CardHeader>
          <HeaderMark src={tbIcon} alt="" aria-hidden="true" />
        </CardHeader>
        <CardBody>
          <ScrollContent $stage={scrollStage} $leaving={leaving}>
            <Page>
              <EventTitle>{EVENT_TITLE}</EventTitle>
              <PollCard
                selected={pollSelected}
                saving={pollSaving}
                saved={pollSaved}
                votes={pollSaved ? POLL_VOTES_END : POLL_VOTES_START}
                showCursor={pollCursor}
              />
            </Page>
            <Page>
              <NpsCard
                value={npsValue}
                saving={npsSaving}
                saved={npsSaved}
                votes={npsSaved ? NPS_VOTES_END : NPS_VOTES_START}
                showCursor={npsCursor}
              />
            </Page>
            <Page>
              <TextCard
                typedChars={typedChars}
                caretBlink
                showCursor={textCursor}
              />
            </Page>
          </ScrollContent>
        </CardBody>
      </Card>
      <Caption $visible={showCaption}>{CAPTION_TEXT}</Caption>
    </Frame>
  );
};

export default AttendeeDemo;
